import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TopNav } from '@/components/layout/TopNav';
import { Button } from '@/components/ui/button';
import { FullScreenLoader } from '@/components/ui/full-screen-loader';
import { useAuth } from '@/hooks/use-auth';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'wouter';
export default function Home() {
    const { t } = useTranslation();
    const { currentUser, canViewStudies, isTeleconsultationOnly, isLinkOnly, isLoading, isError, isFetching, } = useCurrentUser();
    const { logout } = useAuth();
    const queryClient = useQueryClient();
    if (isLoading) {
        return _jsx(FullScreenLoader, {});
    }
    // The profile call itself failed AND we have never had one to fall back on —
    // most often a region that holds no user_profile row for this identity.
    // React Query keeps serving the last-good `data` through a background
    // refetch failure, so isError alone would also catch a transient blip on an
    // already-working session; requiring the absence of currentUser here keeps
    // that session on its current screen instead of swapping in this one.
    // Checked before every redirect: the capability flags below are derived
    // from a profile we never got, so sending the user onward would land them
    // in a surface backed by the same failure. Distinct from the no-access
    // branch, which blames the account rather than the request and offers
    // nothing to do about it.
    if (isError && !currentUser) {
        return (_jsxs("div", { className: "bg-background min-h-dvh", children: [_jsx(TopNav, {}), _jsx("div", { className: "flex h-[calc(100dvh-3rem)] items-center justify-center", children: _jsxs("div", { className: "max-w-md space-y-3 px-4 text-center", role: "alert", children: [_jsx(AlertTriangle, { className: "text-muted-foreground mx-auto h-10 w-10" }), _jsx("h2", { className: "text-lg font-semibold", children: t('home.profileLoadFailed') }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('home.profileLoadFailedDesc') }), _jsxs("div", { className: "flex items-center justify-center gap-2 pt-1", children: [_jsx(Button, { disabled: isFetching, onClick: () => void queryClient.invalidateQueries({ queryKey: ['currentUser'] }), children: isFetching ? t('home.profileLoadRetrying') : t('home.profileLoadRetry') }), _jsx(Button, { variant: "outline", onClick: () => void logout(), children: t('nav.logOut') })] })] }) })] }));
    }
    // Link-tier practices get the Link shell, never the full-PACS worklist.
    // Checked before canViewStudies — the Link role includes view_studies.
    if (isLinkOnly) {
        return _jsx(Redirect, { to: "/link/app" });
    }
    if (isTeleconsultationOnly) {
        return _jsx(Redirect, { to: "/teleconsultation" });
    }
    if (canViewStudies) {
        return _jsx(Redirect, { to: "/study/list" });
    }
    return (_jsxs("div", { className: "bg-background min-h-dvh", children: [_jsx(TopNav, {}), _jsx("div", { className: "flex h-[calc(100dvh-3rem)] items-center justify-center", children: _jsxs("div", { className: "space-y-3 text-center", children: [_jsx(ShieldAlert, { className: "text-muted-foreground mx-auto h-10 w-10" }), _jsx("h2", { className: "text-lg font-semibold", children: t('home.noAccess') }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('home.noPermission') })] }) })] }));
}
